import { ReactNode, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { FocusableItem, MenuItem, MenuProps } from '@szhsin/react-menu';

import { RangeCalendar } from '@/design_system/InputDate';
import { dateStringToCalendarDate } from '@/design_system/InputDate/InputDate';
import { DateRangeValue } from '@/design_system/InputDate/InputDateRange';
import IconCheck from '@/icons/Check.svg';

import { FilterRadioGroup, FilterRadioItem } from './FilterRadioItem';
import { FilterDivider, FiltersMenu, FiltersSubMenu } from './FiltersMenu';

export type FilterDateValue = 'custom' | 'all' | 'last-7-days' | 'last-30-days';

export const FilterDate = ({
  icon,
  label,
  button,
  value,
  customValue,
  onChange,
}: {
  icon?: ReactNode;
  label?: ReactNode;
  button?: MenuProps['menuButton'];
  value: FilterDateValue;
  customValue?: DateRangeValue;
  onChange?: (value: FilterDateValue, customValue?: DateRangeValue) => void;
}) => {
  const [isSelectingCustomDate, setIsSelectingCustomDate] = useState(false);

  const base = (
    <>
      {isSelectingCustomDate ? (
        <FocusableItem
          style={{ minWidth: 'fit-content', paddingTop: '1rem', borderRadius: '0.5rem' }}
        >
          {() => (
            <RangeCalendar
              borderless
              value={customValue}
              maxValue={dateStringToCalendarDate(new Date().toISOString())}
              onChange={(value) => {
                if (value) onChange?.('custom', value);
              }}
            />
          )}
        </FocusableItem>
      ) : (
        <FilterRadioGroup value={value} onChange={onChange}>
          <FilterRadioItem value="all">
            <Trans id="filters-menu.filter-date.option.all">All</Trans>
          </FilterRadioItem>
          <FilterRadioItem value="last-7-days">
            <Trans id="filters-menu.filter-date.option.last-7-days">Last 7 days</Trans>
          </FilterRadioItem>
          <FilterRadioItem value="last-30-days">
            <Trans id="filters-menu.filter-date.option.last-30-days">Last 30 days</Trans>
          </FilterRadioItem>
          <FilterDivider sub />
          <MenuItem
            onClick={(e) => {
              setIsSelectingCustomDate(true);
              e.keepOpen = true;
            }}
          >
            <Trans id="filters-menu.filter-date.option.custom">Custom date</Trans>
            {value === 'custom' && <IconCheck />}
          </MenuItem>
        </FilterRadioGroup>
      )}
    </>
  );

  if (button) {
    return (
      <FiltersMenu
        button={button}
        onChange={({ open }) => {
          if (!open) {
            setIsSelectingCustomDate(false);
          }
        }}
      >
        {base}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={icon}
      label={label}
      onChange={({ open }) => {
        if (!open) {
          setIsSelectingCustomDate(false);
        }
      }}
    >
      {base}
    </FiltersSubMenu>
  );
};
